import { Box, Table, Tbody, Td, Text, Th, Thead, Tr, useColorModeValue } from '@chakra-ui/react';
import React from 'react';

interface Column {
  title: string;
  dataIndex: string;
  key: string;
  width: string;
  render?: (_row: any) => React.ReactNode;
}

interface ScrollableTableProps {
  columns: Column[];
  data: any[];
  rowKey: string | ((_row: any) => string);
  noDataText: string;
}

const ScrollableTable: React.FC<ScrollableTableProps> = ({ columns, data, rowKey, noDataText }) => {
  const colorText = useColorModeValue('gray.800', 'white');
  const colorLabel = useColorModeValue('gray.600', 'gray.300');
  const bgEven = useColorModeValue('gray.50', 'gray.700');
  const bgOdd = useColorModeValue('white', 'gray.900');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const scrollbarTrackBg = useColorModeValue('gray.100', 'gray.700');
  const scrollbarThumbBg = useColorModeValue('gray.300', 'gray.500');
  const scrollbarThumbHoverBg = useColorModeValue('gray.400', 'gray.400');
  const headerBg = useColorModeValue('gray.100', 'gray.800');
  const hoverBg = useColorModeValue('gray.100', 'gray.600');
  const rowBorderColor = useColorModeValue('gray.200', 'gray.600');

  const totalWidth = columns.reduce((sum, col) => sum + parseInt(col.width.replace('px', '')), 0);

  return (
    <Box
      sx={{
        '&::-webkit-scrollbar': {
          width: '8px',
          height: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: scrollbarTrackBg,
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: scrollbarThumbBg,
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: scrollbarThumbHoverBg,
        },
        '&::-webkit-scrollbar-corner': {
          background: 'transparent',
        },
      }}
      overflow='auto'
      minW='100%'
      maxW='100%'
      h='100%'
      maxH='100%'
      border='1px solid'
      borderColor={borderColor}
      borderRadius='md'
    >
      <Table minW={`${totalWidth}px`} style={{ tableLayout: 'fixed' }}>
        <Thead pos='sticky' zIndex={1} top={0}>
          <Tr>
            {columns.map((col) => (
              <Th
                key={col.key}
                w={col.width}
                py={{
                  base: '0.5rem',
                  lg: '0.75rem',
                }}
                color={colorLabel}
                fontSize='0.8rem'
                fontWeight='500'
                textTransform='none'
                bg={headerBg}
                borderColor={borderColor}
                borderBottom='1px solid'
                whiteSpace='nowrap'
              >
                {col.title}
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {data && data.length > 0 ? (
            data.map((row, index) => (
              <Tr
                key={typeof rowKey === 'function' ? rowKey(row) : rowKey ? row[rowKey] : index}
                bg={index % 2 === 0 ? bgEven : bgOdd}
                _hover={{
                  bg: hoverBg,
                }}
              >
                {columns.map((col) => (
                  <Td
                    key={col.key}
                    w={col.width}
                    py={2}
                    fontSize={13}
                    borderColor={rowBorderColor}
                    borderBottom='1px solid'
                    whiteSpace='nowrap'
                  >
                    {col.render ? (
                      col.render(row)
                    ) : (
                      <Text color={colorText}>{row[col.dataIndex]}</Text>
                    )}
                  </Td>
                ))}
              </Tr>
            ))
          ) : (
            <Tr>
              <Td py='1.5rem' color={colorLabel} textAlign='center' colSpan={columns.length}>
                {noDataText}
              </Td>
            </Tr>
          )}
        </Tbody>
      </Table>
    </Box>
  );
};

export default ScrollableTable;
